import React, {useEffect, useState} from 'react'
import Moment from 'react-moment'
import {Modal, ModalBody, ModalFooter, ModalHeader} from 'reactstrap'
import {ProjectGuestPortalClient} from '../../infrastracture/api/ProjectGuestPortalClient'

// Same three the Teams client's DurationSelector falls back to when the asset type's check-in
// strategy names none of its own.
const FALLBACK_DURATIONS = [60, 240, 480]

// The Teams HourSelector offers 05:00 to 23:45 in quarter hours, and defaults to 8:30 when it
// is still ahead.
const FIRST_SLOT_MINUTES = 5 * 60
const LAST_SLOT_MINUTES = 23 * 60 + 45
const SLOT_STEP_MINUTES = 15
const DEFAULT_SLOT = '08:30'

// Ported from the Teams client's connectRuleHelper. Connect/project assets are withheld for
// their owning group until 3pm on the working day before the booking date, and the server
// (CreateCheckInPlannerCommand) has no time-of-day logic; it trusts the flag the client sends.
// So both clients have to compute the cutoff the same way or the two will drift apart.
const RELEASE_HOUR = 15

function startOfDay(d: Date) {
  const out = new Date(d)
  out.setHours(0, 0, 0, 0)
  return out
}

/** Walks back to the previous Mon-Fri, so a Monday booking releases on the Friday. */
function previousWorkingDay(d: Date) {
  const out = new Date(d)
  out.setDate(out.getDate() - 1)
  while (out.getDay() === 0 || out.getDay() === 6) out.setDate(out.getDate() - 1)
  return out
}

function shouldIgnoreConnectRule(date: Date, now = new Date()) {
  const target = startOfDay(date)

  // Today, and anything already past, is never withheld.
  if (target.getTime() <= startOfDay(now).getTime()) return true

  const release = previousWorkingDay(target)
  release.setHours(RELEASE_HOUR, 0, 0, 0)

  return now.getTime() >= release.getTime()
}

function slotLabel(minutes: number) {
  const hour = Math.floor(minutes / 60)
  const minute = minutes % 60
  const suffix = hour < 12 ? 'AM' : 'PM'
  const hour12 = hour % 12 === 0 ? 12 : hour % 12

  return `${hour12}:${String(minute).padStart(2, '0')} ${suffix}`
}

function slotValue(minutes: number) {
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${pad(Math.floor(minutes / 60))}:${pad(minutes % 60)}`
}

/**
 * Quarter-hour slots for the day. On today the ones already gone are dropped, which is what
 * stops the picker offering a start time the server would only reject.
 */
function buildTimeSlots(isToday: boolean, now = new Date()) {
  const nowMinutes = now.getHours() * 60 + now.getMinutes()
  const slots: {value: string; label: string}[] = []

  for (let m = FIRST_SLOT_MINUTES; m <= LAST_SLOT_MINUTES; m += SLOT_STEP_MINUTES) {
    if (isToday && m < nowMinutes) continue
    slots.push({value: slotValue(m), label: slotLabel(m)})
  }

  return slots
}

function durationLabel(minutes: number) {
  const hours = minutes / 60
  return `${hours} hour${hours === 1 ? '' : 's'}`
}

/** The [start, end) a guest currently has picked, as real instants — null until both a start
 *  time and a duration are chosen, which a conflict can never overlap. */
function selectedBookingWindow(date: string, startTime: string, duration: number) {
  if (!startTime || !duration) return null

  const start = new Date(`${date}T${startTime}:00`)
  if (isNaN(start.getTime())) return null

  return {start, end: new Date(start.getTime() + duration * 60000)}
}

/**
 * Every open check-in this asset already has for the day, each labelled "8:30 AM (8hrs)" and
 * flagged with whether it overlaps the currently selected start time/duration — the same
 * interval test CreateCheckInPlannerCommand runs server-side (start < otherEnd && otherStart <
 * end). GetAvailableAssets returns every booking per asset up front, so this recomputes purely
 * client-side on every start time/duration change instead of asking the server each time.
 */
function assetBookingLabels(asset: any, activeWindow: {start: Date; end: Date} | null) {
  const bookings = (asset.bookings || []) as any[]

  return bookings
    .map((booking) => {
      if (!booking.checkInDate || !booking.checkOutDue) return null

      const start = new Date(booking.checkInDate)
      const end = new Date(booking.checkOutDue)
      if (isNaN(start.getTime()) || isNaN(end.getTime())) return null

      const minutes = Math.round((end.getTime() - start.getTime()) / 60000)
      if (minutes <= 0) return null

      const hours = minutes / 60
      const durationText = Number.isInteger(hours)
        ? `${hours}hr${hours === 1 ? '' : 's'}`
        : `${Math.floor(hours)}h ${minutes % 60}m`

      const conflicts =
        !!activeWindow &&
        start.getTime() < activeWindow.end.getTime() &&
        activeWindow.start.getTime() < end.getTime()

      return {
        label: `${slotLabel(start.getHours() * 60 + start.getMinutes())} (${durationText})`,
        conflicts,
      }
    })
    .filter((entry: any) => entry !== null) as {label: string; conflicts: boolean}[]
}

export function ProjectGuestBookAssetModal({
  projectId,
  date,
  isOpen,
  assetTypeName,
  onClose,
  onBooked,
}: {
  projectId: string
  /** The day being booked, as YYYY-MM-DD. */
  date: string
  isOpen: boolean
  /** The account's system-default asset type name (e.g. "Desk"), already falling back to
   *  'Asset' in the caller — used in place of the generic word "asset" in the header. */
  assetTypeName: string
  onClose: () => void
  onBooked: () => void
}) {
  const [assets, setAssets] = useState<any[]>([])
  const [loading, setLoading] = useState(false)
  const [booking, setBooking] = useState(false)
  const [error, setError] = useState('')
  const [selectedAssetId, setSelectedAssetId] = useState('')
  const [startTime, setStartTime] = useState('')
  const [duration, setDuration] = useState(0)

  const isToday = date === (() => {
    const pad = (n: number) => String(n).padStart(2, '0')
    const now = new Date()
    return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`
  })()

  const timeSlots = buildTimeSlots(isToday)

  const selectedAsset = assets.find((a: any) => a.assetId === selectedAssetId)

  // Per asset, since the options come from its own asset type's check-in strategy. Teams
  // defaults to the longest of them.
  const durationOptions =
    selectedAsset?.durationOptions?.length > 0 ? selectedAsset.durationOptions : FALLBACK_DURATIONS

  // Recomputed on every render, so it always reflects the start time/duration currently picked
  // without a fetch of its own.
  const bookingWindow = selectedBookingWindow(date, startTime, duration)
  const selectedHasConflict =
    !!selectedAsset && assetBookingLabels(selectedAsset, bookingWindow).some((b) => b.conflicts)

  useEffect(() => {
    if (!isOpen) return

    let cancelled = false

    setLoading(true)
    setError('')
    setSelectedAssetId('')

    ProjectGuestPortalClient.GetAvailableAssets(projectId, date).then((response) => {
      if (cancelled) return

      if (response.successful) setAssets(response.data || [])
      else {
        setAssets([])
        setError('Failed to load available assets for this date.')
      }

      setLoading(false)
    })

    return () => {
      cancelled = true
    }
  }, [isOpen, projectId, date])

  // Parked on the same defaults the Teams pickers use: 8:30 when it is still ahead, otherwise
  // the next slot going. Booking is then one click for anyone happy with a standard day.
  useEffect(() => {
    if (!isOpen) return

    const slots = buildTimeSlots(isToday)
    const preferred = slots.find((slot) => slot.value === DEFAULT_SLOT) || slots[0]
    setStartTime(preferred ? preferred.value : '')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, date])

  // Follows the selected asset: a different asset type can offer a different set of lengths,
  // and holding onto one it doesn't offer would book something nobody picked.
  useEffect(() => {
    if (duration && durationOptions.includes(duration)) return
    setDuration(durationOptions[durationOptions.length - 1])
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAssetId, assets])

  // A start time or duration change can put the current pick in conflict with one of that
  // asset's other bookings for the day — drop the selection rather than leave the Book button
  // armed for something the server would only reject.
  useEffect(() => {
    if (selectedAssetId && selectedHasConflict) setSelectedAssetId('')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startTime, duration])

  const book = async () => {
    if (!selectedAssetId || !startTime || !duration) return

    setBooking(true)
    setError('')

    const response = await ProjectGuestPortalClient.BookAsset(
      projectId,
      selectedAssetId,
      date,
      startTime,
      duration,
      shouldIgnoreConnectRule(new Date(`${date}T00:00:00`))
    )

    setBooking(false)

    if (!response.successful) {
      setError(
        response.validationErrors?.[0]?.message ||
          response.data?.message ||
          'Could not book that asset.'
      )
      return
    }

    // The endpoint answers per booking rather than with a bare pass/fail, so a rejection
    // (withheld for a project, already taken, blocked) arrives with its own reason.
    const failed = (response.data || []).find((result: any) => result.success === false)

    if (failed) {
      setError(failed.reason || 'Could not book that asset.')
      return
    }

    onBooked()
    onClose()
  }

  return (
    <Modal isOpen={isOpen} size='lg' centered>
      <ModalHeader>
        Book {assetTypeName} for <Moment format='ddd DD MMM'>{date}</Moment>
      </ModalHeader>

      <ModalBody>
        {error && <div className='alert alert-danger py-3'>{error}</div>}

        {loading ? (
          <div className='text-center text-muted py-5'>Loading available assets...</div>
        ) : assets.length === 0 ? (
          <div className='text-center text-muted py-5'>
            No assets are available in this project for this day.
          </div>
        ) : (
          <div className='table-responsive' style={{maxHeight: 320, overflowY: 'auto'}}>
            <table className='table table-row-dashed align-middle gs-0 gy-3 mb-0'>
              <thead>
                <tr className='fw-bold text-muted bg-light'>
                  <th className='ps-4 w-40px rounded-start' />
                  <th className='min-w-150px'>Asset</th>
                  <th className='min-w-140px'>Start Time</th>
                  <th className='min-w-120px'>Site</th>
                  <th className='min-w-100px rounded-end'>Level</th>
                </tr>
              </thead>
              <tbody>
                {assets.map((asset: any) => {
                  const bookings = assetBookingLabels(asset, bookingWindow)

                  // Closed off only when the currently selected start time/duration actually
                  // overlaps one of this asset's bookings — an asset with a booking elsewhere
                  // in the day stays pickable for a slot that doesn't conflict with it.
                  const booked = bookings.some((b) => b.conflicts)

                  return (
                    <tr
                      key={asset.assetId}
                      role={booked ? undefined : 'button'}
                      aria-disabled={booked || undefined}
                      className={
                        booked
                          ? 'text-muted'
                          : asset.assetId === selectedAssetId
                            ? 'bg-light-primary'
                            : ''
                      }
                      style={booked ? {opacity: 0.55, cursor: 'not-allowed'} : undefined}
                      title={booked ? 'Conflicts with an existing booking at this time' : undefined}
                      onClick={() => {
                        if (booked) return
                        setSelectedAssetId(asset.assetId)
                      }}
                    >
                      <td className='ps-4'>
                        <div className='form-check form-check-sm form-check-custom'>
                          <input
                            className='form-check-input'
                            type='radio'
                            name={`guest-book-asset-${projectId}`}
                            checked={asset.assetId === selectedAssetId}
                            disabled={booked}
                            onChange={() => setSelectedAssetId(asset.assetId)}
                            aria-label={`Select ${asset.assetName}`}
                          />
                        </div>
                      </td>
                      <td className={booked ? 'fw-bold' : 'text-dark fw-bold'}>
                        {asset.assetLetter ? `${asset.assetLetter}: ` : ''}
                        {asset.assetName}
                        {booked && (
                          <span className='badge badge-light-danger ms-2 fs-8'>Booked</span>
                        )}
                      </td>
                      <td className='text-gray-600'>
                        {bookings
                          .filter((b) => b.conflicts)
                          .map((b, i) => (
                            <div key={i} className='text-danger fw-bold'>
                              {b.label}
                            </div>
                          ))}
                      </td>
                      <td className='text-gray-600'>{asset.siteName}</td>
                      <td className='text-gray-600'>{asset.floorName}</td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </ModalBody>

      <ModalFooter className='d-flex flex-wrap justify-content-between gap-3'>
        <div className='d-flex flex-wrap align-items-center gap-3'>
          <div>
            <label className='form-label fs-8 text-gray-600 mb-1' htmlFor={`guest-book-time-${projectId}`}>
              Start time
            </label>
            <select
              id={`guest-book-time-${projectId}`}
              className='form-select form-select-sm'
              style={{minWidth: 130}}
              value={startTime}
              disabled={timeSlots.length === 0}
              onChange={(e) => setStartTime(e.target.value)}
            >
              {timeSlots.map((slot) => (
                <option key={slot.value} value={slot.value}>
                  {slot.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className='form-label fs-8 text-gray-600 mb-1' htmlFor={`guest-book-duration-${projectId}`}>
              Duration
            </label>
            <select
              id={`guest-book-duration-${projectId}`}
              className='form-select form-select-sm'
              style={{minWidth: 130}}
              value={duration}
              onChange={(e) => setDuration(Number(e.target.value))}
            >
              {durationOptions.map((option: number) => (
                <option key={option} value={option}>
                  {durationLabel(option)}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className='d-flex align-items-center gap-2'>
          <button type='button' className='btn btn-sm btn-light' onClick={onClose}>
            Cancel
          </button>
          <button
            type='button'
            className='btn btn-sm btn-primary'
            disabled={!selectedAssetId || !startTime || !duration || booking || selectedHasConflict}
            onClick={book}
          >
            {booking ? 'Booking...' : 'Book'}
          </button>
        </div>
      </ModalFooter>
    </Modal>
  )
}
